import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "imprimante-non-detectee",
  silo: "materiel",
  isPillar: true,
  h1: "Imprimante non détectée par Windows : comment résoudre le problème ?",
  metaTitle: "Imprimante non détectée Windows : solutions simples",
  metaDescription: "Windows ne voit pas votre imprimante ? Pilotes, connexion USB, Wi-Fi... Suivez notre guide pour rétablir l'impression en quelques minutes.",
  excerpt: "Votre imprimante était parfaitement reconnue hier, et ce matin Windows fait comme si elle n'existait pas ? Ce problème courant a plusieurs causes simples -- et presque toujours une solution rapide.",
  publishedAt: "2024-02-01",
  readingMinutes: 6,
  relatedSlugs: ["webcam-micro-ne-fonctionne-pas", "pas-de-son-ordinateur", "logiciel-plante-ne-souvre-pas", "mise-a-jour-windows-bloquee"],
  faq: [
    {
      question: "Mon imprimante était reconnue hier et plus aujourd'hui -- que s'est-il passé ?",
      answer: "Une mise à jour Windows peut parfois désinstaller ou corrompre les pilotes d'imprimante. C'est la cause la plus fréquente d'une imprimante qui disparaît du jour au lendemain. Réinstaller le pilote depuis le site du fabricant résout généralement le problème.",
    },
    {
      question: "L'imprimante est branchée en USB mais Windows ne la détecte pas -- pourquoi ?",
      answer: "Essayez un autre port USB, de préférence à l'arrière du PC (plus stables). Certaines imprimantes nécessitent d'être allumées avant d'être branchées. Vérifiez aussi que le câble USB n'est pas endommagé.",
    },
    {
      question: "Comment ajouter une imprimante WiFi sur Windows ?",
      answer: "Paramètres > Bluetooth et appareils > Imprimantes et scanners > Ajouter une imprimante. Assurez-vous que l'imprimante et le PC sont sur le même réseau WiFi. Si elle n'apparaît pas, saisissez manuellement l'adresse IP de l'imprimante.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Une imprimante non détectée est l'un des problèmes les plus fréquents que nous traitons. La bonne nouvelle : dans 90% des cas, la solution prend moins de 10 minutes.",
    },
    {
      type: "h2",
      text: "Vérifications de base à faire en premier",
    },
    {
      type: "ul",
      items: [
        "L'imprimante est allumée et son écran (ou voyant) indique qu'elle est prête",
        "Le câble USB est bien branché des deux côtés (imprimante ET PC)",
        "Pour une imprimante WiFi : elle est sur le même réseau WiFi que votre PC",
        "La file d'impression est vide (Panneau de configuration > Périphériques et imprimantes)",
      ],
    },
    {
      type: "h2",
      text: "Solution 1 : utiliser le dépanneur d'imprimante Windows",
    },
    {
      type: "p",
      text: "Windows propose un outil de dépannage spécialisé pour les imprimantes. Allez dans Paramètres > Système > Dépannage > Autres dépanneurs > Imprimante > Exécuter. L'outil détecte et corrige automatiquement la plupart des problèmes courants.",
    },
    {
      type: "h2",
      text: "Solution 2 : supprimer et réinstaller l'imprimante",
    },
    {
      type: "p",
      text: "Paramètres > Bluetooth et appareils > Imprimantes et scanners. Cliquez sur votre imprimante > Supprimer. Redémarrez le PC. Rebranchez l'imprimante ou reconnectez-la au WiFi -- Windows la détectera à nouveau et installera le pilote automatiquement.",
    },
    {
      type: "h2",
      text: "Solution 3 : mettre à jour ou réinstaller le pilote",
    },
    {
      type: "p",
      text: "Rendez-vous sur le site du fabricant de votre imprimante (HP, Canon, Epson, Brother...) et téléchargez le pilote correspondant à votre modèle et votre version de Windows. L'installation du pilote officiel est souvent bien plus complète que celui proposé par Windows Update.",
    },
    {
      type: "callout",
      variant: "tip",
      title: "Pilote universel vs pilote constructeur",
      text: "Windows installe parfois un pilote universel qui ne gère pas toutes les fonctionnalités de votre imprimante (recto-verso, qualité photo, etc.). Le pilote du fabricant est toujours préférable.",
    },
    {
      type: "h2",
      text: "Solution 4 : redémarrer le service Spouleur d'impression",
    },
    {
      type: "p",
      text: "Le spouleur d'impression gère la file d'attente des documents. S'il est bloqué, aucune imprimante ne fonctionne. Ouvrez l'invite de commandes en admin et tapez :\n• net stop spooler\n• del /Q /F /S '%systemroot%\\System32\\spool\\PRINTERS\\*.*'\n• net start spooler",
    },
    {
      type: "h2",
      text: "Imprimante réseau non détectée en entreprise",
    },
    {
      type: "p",
      text: "Dans un environnement réseau, une imprimante partagée peut disparaître après une mise à jour Windows ou un changement de configuration réseau. Nos techniciens peuvent intervenir sur site pour reconfigurer la connexion à l'imprimante réseau, que ce soit via IP fixe ou via un serveur d'impression. Consultez aussi notre guide sur les problèmes [webcam et microphone](slug:webcam-micro-ne-fonctionne-pas) qui partagent souvent les mêmes causes (pilotes, mises à jour).",
    },
  ],
};

export default article;
